import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Alert,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Select,
  MenuItem,
  AppBar,
  Toolbar,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

// Assets
import logo from "../../assets/logo-full.png";
import frFlag from "../../assets/fr-flag.png";
import ukFlag from "../../assets/uk-flag.png";
import plFlag from "../../assets/pl-flag.png";

const API_BASE = import.meta?.env?.VITE_API_URL || process.env.REACT_APP_VENDOR_SYB_API || "";
const CLIENT_PORTAL_URL = import.meta?.env?.VITE_CLIENT_PORTAL_URL || "/login";
const BRAND = "#449F2D";

function ProcessingNavbar() {
  const { t, i18n } = useTranslation("parcours-client/PaymentProcessingPage");
  const supported = (i18n.options?.supportedLngs || ["fr", "en", "pl"]).filter(
    (l) => !["cimode", "dev"].includes(l)
  );
  const current = supported.includes(i18n.language) ? i18n.language : "fr";
  const flagIcons = { fr: frFlag, en: ukFlag, pl: plFlag };

  return (
    <AppBar position="fixed" sx={{ backgroundColor: "#fff", boxShadow: "0 2px 6px rgba(0,0,0,0.05)", borderBottom: "1px solid #e0e0e0", zIndex: 1100 }}>
      <Toolbar disableGutters sx={{ minHeight: 90, position: "relative" }}>
        <Box sx={{ width: "100%", maxWidth: "1200px", mx: "auto", px: "24px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "relative" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box component="img" src={logo} alt="Logo" sx={{ height: 70 }} />
          </Box>
          <Box sx={{ position: "absolute", left: "50%", transform: "translateX(-50%)", display: "flex", alignItems: "center", gap: 1, pointerEvents: "none" }}>
            <LockOutlinedIcon sx={{ color: BRAND }} />
            <Typography variant="h6" sx={{ fontWeight: "bold", color: BRAND, whiteSpace: "nowrap" }}>
              {t("topbar.secure")}
            </Typography>
          </Box>
          <Select
            value={current}
            onChange={(e) => i18n.changeLanguage(e.target.value)}
            variant="standard"
            disableUnderline
            sx={{ display: "flex", alignItems: "center", fontWeight: "bold", color: BRAND, fontSize: "1rem", minWidth: 90, "& .MuiSelect-icon": { color: BRAND, right: 6 } }}
            renderValue={(val) => (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box component="img" src={flagIcons[val]} alt={val} sx={{ width: 26, height: 18, objectFit: "cover" }} />
                <span>{String(val).toUpperCase()}</span>
              </Box>
            )}
          >
            <MenuItem value="fr"><Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><img src={frFlag} alt="FR" width="20" height="14" /> Français</Box></MenuItem>
            <MenuItem value="en"><Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><img src={ukFlag} alt="EN" width="20" height="14" /> English</Box></MenuItem>
            <MenuItem value="pl"><Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><img src={plFlag} alt="PL" width="20" height="14" /> Polski</Box></MenuItem>
          </Select>
        </Box>
      </Toolbar>
    </AppBar>
  );
}

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function PaymentProcessingPage() {
  const { t } = useTranslation("parcours-client/PaymentProcessingPage");
  const q = useQuery();
  const navigate = useNavigate();

  const sid = q.get("sid") || "";
  const rawRedir = q.get("redir");
  const redir = rawRedir ? decodeURIComponent(rawRedir) : null;

  const [status, setStatus] = useState("pending");
  const [error, setError] = useState("");
  const [autoOpenBlocked, setAutoOpenBlocked] = useState(false);

  const pollTimerRef = useRef(null);
  const openedRef = useRef(false);

  const getSavedPayload = () => {
    try {
      const raw = sessionStorage.getItem("syb:lastPaymentPayload");
      if (!raw) return null;
      const obj = JSON.parse(raw);
      return obj?.payload || null;
    } catch {
      return null;
    }
  };

  const retryLastPayment = async () => {
    setError("");
    const payload = getSavedPayload();
    if (!payload) {
      setError(t("retry.noPayload"));
      return;
    }
    try {
      const { data } = await axios.post(`${API_BASE}/subscriptions/by-customer`, payload, {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      });
      const out = data?.data || {};
      if (out.paymentUrl) window.open(out.paymentUrl, "_blank", "noopener,noreferrer");
      if (out.subscriptionId) {
        navigate(`/paiement/en-cours?sid=${encodeURIComponent(out.subscriptionId)}`, { replace: true });
      } else {
        setError(t("retry.invalidResponse"));
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "";
      setError(t("errors.requestFailed", { msg }));
    }
  };

  useEffect(() => {
    if (!redir || openedRef.current) return;
    openedRef.current = true;
    const w = window.open(redir, "_blank", "noopener,noreferrer");
    if (!w) setAutoOpenBlocked(true);
  }, [redir]);

  // ✅ helper: on sauvegarde l'email durablement avant de rediriger
  const persistEmailAndGo = (email) => {
    try {
      if (email) localStorage.setItem("syb:lastPaymentEmail", email);
    } catch {}
    const qs = email ? `?email=${encodeURIComponent(email)}` : "";
    navigate(`/paiement/succes${qs}`, { replace: true, state: { email } });
    try {
      sessionStorage.removeItem("syb:lastPaymentPayload");
    } catch {}
  };

  useEffect(() => {
    if (!sid) return;
    let tries = 0;
    const maxTries = 120;

    const tick = async () => {
      tries += 1;
      try {
        const { data } = await axios.get(`${API_BASE}/subscriptions/${sid}/status`, { withCredentials: true });
        const ps = String(data?.data?.payment_status || "pending").toLowerCase();

        if (ps === "success") {
          setStatus("success");
          // récup email depuis le payload sauvegardé
          let email = null;
          try {
            const raw = sessionStorage.getItem("syb:lastPaymentPayload");
            if (raw) email = JSON.parse(raw)?.payload?.subscriber?.email || null;
          } catch {}
          persistEmailAndGo(email);
          return;
        }
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || "";
        setError(t("errors.requestFailed", { msg }));
      }
      if (tries < maxTries) pollTimerRef.current = setTimeout(tick, 5000);
    };

    tick();
    return () => { if (pollTimerRef.current) clearTimeout(pollTimerRef.current); };
  }, [sid, navigate, t]);

  const copyLink = async () => {
    if (!redir) return;
    try { await navigator.clipboard.writeText(redir); } catch {}
  };

  const manualRefresh = async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/subscriptions/${sid}/status`, { withCredentials: true });
      const ps = String(data?.data?.payment_status || "pending").toLowerCase();
      if (ps === "success") {
        setStatus("success");
        let email = null;
        try {
          const raw = sessionStorage.getItem("syb:lastPaymentPayload");
          if (raw) email = JSON.parse(raw)?.payload?.subscriber?.email || null;
        } catch {}
        persistEmailAndGo(email);
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "";
      setError(t("errors.requestFailed", { msg }));
    }
  };

  if (!sid) {
    return (
      <>
        <ProcessingNavbar />
        <Box sx={{ display: "flex", justifyContent: "center", px: 2, mt: 12 }}>
          <Paper elevation={0} sx={{ width: "100%", maxWidth: 720, p: 5, borderRadius: 3, textAlign: "center" }}>
            <Typography variant="h6" fontWeight={800} mb={1}>
              {t("invalidLink.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {t("invalidLink.body")}
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button variant="contained" onClick={retryLastPayment}>
                {t("actions.retryPayment")}
              </Button>
              <Button variant="outlined" href={CLIENT_PORTAL_URL}>
                {t("success.gotoPortal")}
              </Button>
            </Stack>
          </Paper>
        </Box>
      </>
    );
  }

  return (
    <>
      <ProcessingNavbar />
      <Box sx={{ display: "flex", justifyContent: "center", px: 2, mt: 12 }}>
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 720,
            borderRadius: 3,
            px: { xs: 3, md: 6 },
            py: { xs: 5, md: 6 },
            backgroundColor: "#F8F8F8",
            border: "1px solid #eee",
            textAlign: "center",
          }}
        >
          {status === "pending" && (
            <>
              <Typography variant="h5" fontWeight={800} mb={1}>
                {t("title")}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                {redir ? t("lead.card") : t("lead.blik")}
              </Typography>
              <Box sx={{ my: 3 }}><CircularProgress /></Box>
              <Alert severity="info" sx={{ mb: 3 }}>{t("mailInfo")}</Alert>

              {redir ? (
                <Stack spacing={2} alignItems="center">
                  <Button onClick={() => window.open(redir, "_blank", "noopener,noreferrer")} variant="contained">
                    {t("actions.openPayment")}
                  </Button>
                  {autoOpenBlocked && (
                    <Typography variant="caption" color="text.secondary">
                      {t("popupBlocked")}
                    </Typography>
                  )}
                  <TextField value={redir} size="small" fullWidth InputProps={{ readOnly: true }} />
                  <Button onClick={copyLink} variant="text">{t("actions.copyLink")}</Button>
                  <Stack direction="row" spacing={2} justifyContent="center">
                    <Button onClick={manualRefresh} variant="outlined">{t("actions.refresh")}</Button>
                    <Button onClick={retryLastPayment} variant="contained">{t("actions.retryPayment")}</Button>
                    <Button onClick={() => window.history.back()} variant="outlined">{t("actions.back")}</Button>
                  </Stack>
                </Stack>
              ) : (
                <Stack direction="row" spacing={2} justifyContent="center">
                  <Button onClick={manualRefresh} variant="outlined">{t("actions.refresh")}</Button>
                  <Button onClick={retryLastPayment} variant="contained">{t("actions.retryPayment")}</Button>
                  <Button onClick={() => window.history.back()} variant="outlined">{t("actions.back")}</Button>
                </Stack>
              )}

              {error && (
                <Typography variant="caption" color="error" display="block" mt={2}>
                  {error}
                </Typography>
              )}
            </>
          )}
        </Paper>
      </Box>
    </>
  );
}